import { invalidate } from '$app/navigation';
import type { CitationAttrs } from '#src/types/markdown.ts';
import { processStreamedResponse } from '#lib/process-streamed-response.ts';
import { globalState } from '#lib/util/global-state.svelte.ts';
import { handleApiError } from '#lib/util/error-handling/handle-api-error.ts';
import { dedupe } from '#lib/util/dedupe';
import { Fetchable } from '#lib/util/fetchable';
import { nid } from '#lib/util/nanoid';
import { citationHash } from '#src/contexts/citation-registry.context.svelte.ts';
import { ConversationApiService } from '#src/services/conversation-api.service.svelte.ts';
import { PbCoreApiService } from '#src/services/pb-core-api.service.svelte.ts';
import type { ConversationRecord, ConversationTokenUsage } from '#src/types/conversation.ts';
import { ChatMessage, type AssetRef } from '#src/types/chat.ts';
import type { ChunkDTO, DocumentOverview, RetrievalContext } from '#src/types/documents.ts';
import type {
	CitationOccurrence,
	CitedSource,
	SecondarySource,
	SourceBlock,
	SourceDocument
} from '#src/types/sources.ts';
import { getHighlightColor, getWebHighlightColor } from '#src/util/colors.ts';
import {
	mergeRetrievalContext,
	mergeSecondarySources,
	mergeSources
} from '#src/util/merge-sources.ts';
import { injectHashesIntoText, parseCitationsFromText } from '#src/util/parse-citations.ts';
import { getContext, setContext, untrack } from 'svelte';

export enum ChatStep {
	INTERVIEW,
	IMAGE_GENERATION,
	UPLOAD_INTENTION,
	UPLOAD_CONFIRMATION,
	UPLOAD_FINISHED
}

export class ChatContext {
	private initialMessage = `Hallo, wie kann ich dir helfen?`;
	readonly fetchable = new Fetchable();

	conversation: ConversationRecord = $state();
	tokenUsage: ConversationTokenUsage = $state({
		input_tokens: 0,
		output_tokens: 0,
		cache_creation_input_tokens: 0,
		cache_read_input_tokens: 0,
		credits: 0
	});
	standalone: boolean = $state(false);
	chatPlaceholder: string | undefined = $state(undefined);
	private currentAbortController: AbortController | null = $state(null);
	readonly isStreaming = $derived(this.currentAbortController !== null);

	readonly presentableMessages = $derived(
		this.conversation?.messages.filter(
			(m) =>
				(m.role !== 'system' && !!m.content) ||
				!!m.reasoning_content ||
				(m.role === 'assistant' && !!m.toolCalls?.length)
		)
	);

	readonly latestAssistantMessage = $derived(
		this.conversation?.messages.toReversed().find((m) => m.role === 'assistant')
	);

	readonly assets = $derived((this.conversation?.messages ?? []).flatMap((m) => m.assets ?? []));

	private citations: CitationAttrs[] = $state([]);

	private rebuildCitations() {
		// Pure write – no read of this.citations, so the $effect that calls
		// rebuildCitations() doesn't track citations as a dependency.
		this.citations = dedupe(
			(this.conversation?.messages ?? [])
				.filter((msg) => msg.role === 'assistant' && !!msg.content)
				.flatMap((msg) => parseCitationsFromText(msg.content!)),
			citationHash
		);
	}

	private registerCitations(text: string) {
		const newCitations = dedupe([...this.citations, ...parseCitationsFromText(text)], citationHash);
		if (newCitations.length > this.citations.length) {
			this.citations = newCitations;
		}
	}

	private fullHashInjection() {
		this.conversation.messages = this.conversation.messages.map((msg) => {
			if (msg.role !== 'assistant' || !msg.content) {
				return msg;
			}
			return { ...msg, content: injectHashesIntoText(msg.content) };
		});
	}

	// All cited sources derived from parsed citations + conversation metadata.
	readonly citedSources: CitedSource[] = $derived.by(() => {
		// Group citations by resolved source identity.
		// KB citations are grouped by doc_id (multiple chunks → one source entry).
		// Web citations are grouped by web_id.
		const groups = new Map<string, CitationAttrs[]>();

		for (const reg of this.citations) {
			let groupKey: string;
			if (reg.webId != null) {
				groupKey = `web:${reg.webId}`;
			} else {
				const docId =
					reg.docId ??
					this.conversation?.sources?.find(
						(s) => reg.chunkId != null && s.chunk_ids.includes(reg.chunkId)
					)?.doc_id;
				groupKey = `kb:${docId ?? reg.chunkId ?? 'unknown'}`;
			}
			if (!groups.has(groupKey)) {
				groups.set(groupKey, []);
			}
			groups.get(groupKey)!.push(reg);
		}

		const result: CitedSource[] = [];
		let kbIdx = 0;
		let webIdx = 0;

		for (const [groupKey, regs] of groups) {
			const isWeb = groupKey.startsWith('web:');
			const citation_index = isWeb ? ++webIdx : ++kbIdx;
			const citation_color = isWeb
				? getWebHighlightColor(webIdx - 1)
				: getHighlightColor(kbIdx - 1);

			const quotedCount = regs.filter((r) => r.quote != null).length;
			const shouldAddSubIndices = quotedCount >= 1 && regs.length >= 2;
			let quotedIdx = 0;
			const occurrences: CitationOccurrence[] = regs.map((reg) => {
				const type = reg.webId != null ? 'web' : reg.chunkId != null ? 'chunk' : 'document';
				const id = reg.webId ?? reg.chunkId ?? reg.docId ?? 0;
				const sub_index =
					shouldAddSubIndices && reg.quote != null ? String.fromCharCode(65 + quotedIdx++) : '';
				return {
					hash: citationHash(reg),
					sub_index,
					quotedText: reg.quote,
					chunkText: reg.chunkId != null ? this.chunkTexts.get(reg.chunkId) : undefined,
					sourceBlocks:
						reg.chunkId != null
							? (this.chunkMap.get(reg.chunkId)?.chunk_meta?.source_blocks as
									SourceBlock[] | undefined)
							: undefined,
					type,
					id
				};
			});

			if (isWeb) {
				const secondarySrc = this.conversation?.secondary_sources?.find(
					(s) => s.id === regs[0].webId
				);
				result.push({
					citation_index,
					citation_color,
					occurrences,
					title: secondarySrc?.title ?? '',
					web_id: regs[0].webId,
					url: secondarySrc?.url,
					description: secondarySrc?.description,
					fetched_at: secondarySrc?.fetched_at,
					secondary_source: secondarySrc
				});
			} else {
				const docId =
					regs[0].docId ??
					this.conversation?.sources?.find(
						(s) => regs[0].chunkId != null && s.chunk_ids.includes(regs[0].chunkId)
					)?.doc_id;
				const doc = docId != null ? this.documentMap.get(docId) : undefined;
				const convSource = this.conversation?.sources?.find((s) => s.doc_id === docId);
				result.push({
					citation_index,
					citation_color,
					occurrences,
					title: doc?.res_name ?? '',
					doc_id: docId,
					chunk_ids: convSource?.chunk_ids ?? []
				});
			}
		}

		return result;
	});

	readonly citationInfoMap = $derived(
		new Map(
			this.citedSources.flatMap((source) =>
				source.occurrences.map((occurrence) => [occurrence.hash, { source, occurrence }] as const)
			)
		)
	);

	getCitationInfo(hash: string): { source: CitedSource; occurrence: CitationOccurrence } | null {
		return this.citationInfoMap.get(hash) ?? null;
	}

	// Lookup maps for documents and chunks
	readonly documentMap = $derived(
		new Map<number, DocumentOverview>(
			(this.conversation?.retrievalContext?.documents ?? []).map((d) => [d.id, d])
		)
	);

	readonly chunkMap = $derived(
		new Map<number, ChunkDTO>(
			(this.conversation?.retrievalContext?.chunks ?? []).map((c) => [c.id, c])
		)
	);

	readonly chunkTexts = $derived(
		new Map<number, string>(
			(this.conversation?.retrievalContext?.chunks ?? []).map((c) => [c.id, c.text])
		)
	);

	private convoApiService: ConversationApiService;
	private pbApiService: PbCoreApiService;

	constructor(baseUrl?: string, apiKey?: string, initialConvo?: ConversationRecord) {
		// we need a specific instance here, because it might be instantiated by the standalone component
		this.convoApiService = new ConversationApiService(baseUrl, apiKey);
		this.pbApiService = new PbCoreApiService(baseUrl, apiKey);
		this.conversation = initialConvo;
		$effect(() => {
			// Rebuild citation index when the active conversation changes (covers initial load
			// and navigation between conversations). The conversation id is the tracked dependency.
			void this.conversation?.id;
			if (!this.conversation) {
				this.citations = [];
				return;
			}

			untrack(() => {
				this.fullHashInjection();
				this.rebuildCitations();
			});
		});
	}

	/**
	 * Get a completion from the llm.
	 * @param newMessage the message not yet persisted server-side for this turn —
	 *   undefined for retry/resume, since the server already has the full history
	 *   and reconstructs it from storage rather than trusting the client to resend
	 *   it. Never more than one message: models don't support back-to-back
	 *   same-role turns, so multi-part input belongs inside this one message's
	 *   content, not as separate messages.
	 * @param retry if true, hits the dedicated retry endpoint instead — the server
	 *   drops the trailing assistant message itself before regenerating.
	 * @returns
	 */
	private async getCompletion(
		newMessage: ChatMessage | undefined,
		appendResponse = false,
		retry = false
	) {
		const tempId = nid();
		let accMessage: ChatMessage = {
			created: new Date(),
			id: tempId,
			status: 'pending',
			content: '',
			reasoning_content: '',
			role: 'assistant',
			type: 'message'
		};
		if (appendResponse) {
			this.appendMessage(accMessage);
		}

		// Create abort controller for this request
		const abortController = new AbortController();
		this.currentAbortController = abortController;

		try {
			const response = retry
				? await this.convoApiService.retryChat(
						this.conversation.id,
						abortController.signal,
						globalState.enabledSecondarySources
					)
				: await this.convoApiService.chatRequest(
						this.conversation.id,
						newMessage,
						this.conversation.model,
						this.conversation.instance,
						abortController.signal,
						globalState.enabledSecondarySources
					);

			await handleApiError(response);

			const stream = processStreamedResponse(response, accMessage, abortController);

			for await (const {
				eventData,
				eventType,
				message,
				sources,
				secondarySources,
				retrievalContext
			} of stream) {
				if (eventType === 'conversation.created') {
					this.conversation.id = eventData.id as string;
				}

				if (retrievalContext) {
					this.setRetrievalContext(retrievalContext);
				}

				if (sources) {
					this.appendSources(sources);
				}

				if (secondarySources) {
					this.appendSecondarySources(secondarySources);
				}

				if (eventType === 'rag.topic') {
					const { topic_description } = eventData as { topic_description: string };
					if (!this.conversation.title) {
						this.conversation.title = topic_description;
					}
				}
				if (eventType === 'rag.usage') {
					const { input_tokens, output_tokens } = eventData as {
						input_tokens: number;
						output_tokens: number;
					};
					this.tokenUsage.input_tokens += input_tokens;
					this.tokenUsage.output_tokens += output_tokens;
				}
				accMessage = { ...message, content: injectHashesIntoText(message.content) };
				if (appendResponse) {
					this.upsertMessage(accMessage, tempId);
					if (accMessage.content) {
						this.registerCitations(accMessage.content);
					}
				}
			}

			this.rebuildCitations();
			await invalidate('update:conversations');
			await invalidate(`update:conversation:${this.conversation.id}`);

			return accMessage;
		} catch (e) {
			// Only remove message and re-throw if it wasn't aborted by user
			const isAbortError = e instanceof Error && e.name === 'AbortError';
			if (!isAbortError) {
				this.removeMessage(tempId);
				throw e;
			}
		} finally {
			// Clear abort controller when stream completes
			this.currentAbortController = null;
		}
	}

	upsertMessage(message: ChatMessage, tempId?: string) {
		if (message.id) {
			if (tempId && tempId !== message.id) {
				// Real ID differs from temp - check if temp message still exists
				const tempExists = this.conversation.messages.some((m) => m.id === tempId);
				if (tempExists) {
					return this.updateMessage(tempId, message);
				}

				// Temp message was already replaced, fall through to update by real ID
			}
			return this.updateMessage(message.id, message);
		}

		return this.appendMessage(message);
	}

	appendMessage(message: ChatMessage) {
		const id = message.id || nid();
		this.conversation.messages = [...this.conversation.messages, { ...message, id }];
		return id;
	}

	updateMessage(id: string, messageUpdate: Partial<ChatMessage>) {
		const i = this.conversation.messages.findIndex((m) => m.id === id);
		if (i > -1) {
			this.conversation.messages[i] = { ...this.conversation.messages[i], ...messageUpdate };
			return messageUpdate.id;
		}
		this.appendMessage({ id, ...messageUpdate } as ChatMessage);
	}

	removeMessage(id: string) {
		this.conversation.messages = this.conversation.messages.filter((m) => m.id !== id);
		this.rebuildCitations();
	}

	appendSources(sources: SourceDocument[]) {
		this.conversation.sources = mergeSources([...this.conversation.sources, ...sources]);
	}

	appendSecondarySources(sources: SecondarySource[]) {
		this.conversation.secondary_sources = mergeSecondarySources([
			...(this.conversation.secondary_sources ?? []),
			...sources
		]);
	}

	setRetrievalContext(context: RetrievalContext) {
		this.conversation.retrievalContext = mergeRetrievalContext(
			this.conversation.retrievalContext,
			context
		);
	}

	reset() {
		this.conversation.messages = [];
		this.upsertMessage(new ChatMessage('assistant', this.initialMessage));
	}

	/** `assets`: files the user attached to this message (see PendingAttachment). */
	async chat(userMessage?: string, assets: AssetRef[] = []) {
		let newMessage: ChatMessage | undefined;
		if (userMessage) {
			newMessage = new ChatMessage('user', userMessage);
			if (assets.length) {
				newMessage.assets = assets;
			}
			this.upsertMessage(newMessage);
		}

		return this.getCompletion(newMessage, true);
	}

	async abort() {
		if (this.currentAbortController) {
			// Signal RAG to stop the agentic loop early so usage is recorded up to this point.
			// Fire-and-forget — don't await, just let it race ahead of the fetch abort.
			if (this.conversation.id) {
				this.convoApiService.abortChat(this.conversation.id).catch(() => {});
			}

			this.currentAbortController.abort();

			// Mark the last assistant message as incomplete
			const lastMessage = this.conversation.messages.at(-1);
			if (lastMessage?.role === 'assistant') {
				this.updateMessage(lastMessage.id, {
					status: 'incomplete'
				});

				// Persist to database
				await this.updateConversation({
					messages: this.conversation.messages
				});
			}

			this.currentAbortController = null;
		}
	}

	async retry() {
		this.fetchable.fetch(async () => {
			if (this.conversation.messages.at(-1)?.role === 'assistant') {
				this.conversation.messages.pop();
			}
			this.fetchable.error = null;
			// The server drops its own trailing assistant message via the
			// dedicated retry endpoint — no need to persist the local pop first.
			await this.getCompletion(undefined, true, true);
		});
	}

	async updateConversation(params: Partial<ConversationRecord>) {
		this.conversation.title = params.title || this.conversation.title;
		this.conversation.model = params.model || this.conversation.model;
		this.pbApiService.updateConversation(this.conversation.id, params);
		await invalidate(`update:conversation:${this.conversation.id}`);
		await invalidate('update:conversations');
	}
}

export const CHAT_CONTEXT_KEY = Symbol('chat');

export function getChatContext() {
	return getContext<ChatContext>(CHAT_CONTEXT_KEY);
}

export function setChatContext(ctx: ChatContext) {
	setContext(CHAT_CONTEXT_KEY, ctx);
	return ctx;
}
